import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FolderOpen, Calculator as Calc, LogOut, User as UserIcon, LogIn, Sun } from "lucide-react";
import { GSCE_LOGO_DATAURL } from "../assets/gsce-logo.js";
import { useAuth, loginWithGoogle } from "../lib/auth";

export default function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (pathname === "/login") return null;

  return (
    <header
      className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-slate-100"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="navbar-logo">
          <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
            <img src={GSCE_LOGO_DATAURL} alt="GSCE" className="h-9 w-9 object-contain" />
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-display font-bold text-slate-900 text-base truncate">
              GSCE <span className="text-slate-400 font-medium">·</span>{" "}
              <span className="text-blue-700">PAC</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-data hidden sm:block">
              Dimensionnement RGE · BAR-TH-171
            </div>
          </div>
        </Link>

        <nav className="flex items-center gap-1" data-testid="navbar-links">
          {user && (
            <>
              <Link
                to="/"
                data-testid="nav-projects"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  pathname === "/"
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <FolderOpen className="h-4 w-4" />
                <span className="hidden sm:inline">Projets</span>
              </Link>
              <Link
                to="/calculator"
                data-testid="nav-calculator"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  pathname.startsWith("/calculator")
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <Calc className="h-4 w-4" />
                <span className="hidden sm:inline">Nouveau</span>
              </Link>
            </>
          )}
          <Link
            to="/168"
            data-testid="nav-bar168"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              pathname === "/168"
                ? "bg-amber-50 text-amber-700"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <Sun className="h-4 w-4" />
            <span className="hidden md:inline">Solaire BAR-168</span>
            <span className="md:hidden">BAR-168</span>
          </Link>

          {/* User menu / Login button */}
          {user ? (
            <div className="relative ml-1" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm hover:bg-slate-50"
                data-testid="user-menu-trigger"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name || user.email}
                    className="h-8 w-8 rounded-full object-cover border border-slate-200"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold">
                    {(user.name || user.email)[0]?.toUpperCase()}
                  </div>
                )}
                <div className="hidden md:block text-left leading-tight">
                  <div className="font-medium text-slate-900 text-sm max-w-[140px] truncate">
                    {user.name || user.email}
                  </div>
                </div>
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-56 rounded-xl bg-white border border-slate-200 shadow-lg overflow-hidden"
                  data-testid="user-menu-dropdown"
                >
                  <div className="px-3 py-2.5 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <UserIcon className="h-4 w-4 text-slate-400" />
                      <span className="text-xs text-slate-500 truncate">{user.email}</span>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      setMenuOpen(false);
                      await logout();
                      navigate("/login", { replace: true });
                    }}
                    data-testid="logout-btn"
                    className="w-full text-left px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4" />
                    Se déconnecter
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => loginWithGoogle()}
              data-testid="navbar-login-btn"
              className="ml-1 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-[#3b82f6] hover:bg-blue-600 text-white transition-colors"
            >
              <LogIn className="h-4 w-4" />
              <span className="hidden sm:inline">Se connecter</span>
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}
